import { availableAiReportFigures, type AiReport, type AiReportFigureId } from '@erp/shared';
import type { TFunction } from 'i18next';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { formatNumber } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';
import { CHART_COLORS } from '../reports/_shared/chartColors.js';

/**
 * The figures a report can embed. Every figure renders DETERMINISTICALLY from
 * the stored snapshot — the model only places `[[chart:id]]` markers; it never
 * produces a number a chart shows. Forms follow the data's job: time → area,
 * identity+magnitude → horizontal bars (one chart PER CURRENCY — amounts of
 * different currencies never share an axis), exact small lists → tables,
 * aging buckets → a single-hue lightness ramp (CVD-safe by construction).
 *
 * Colors ride the app's fixed categorical order (--viz-1/--viz-4). The only
 * two-series chart (revenue vs profit) carries a legend + direct labels, which
 * also satisfies the palette validator's light-mode contrast relief. Dark mode
 * re-steps the accent into the dark lightness band — see FIGURE_SCOPE_CSS.
 */

/** Scoped chart palette. Dark darkens the accent into the 0.48–0.67 band (validated). */
export const FIGURE_SCOPE_CSS = `
.rf-scope { --rf-1: var(--viz-1); --rf-2: var(--viz-4); }
[data-theme="dark"] .rf-scope, .dark .rf-scope { --rf-2: color-mix(in oklab, var(--viz-4) 72%, black); }
`;

// ---- snapshot section shapes (guarded — a figure silently skips on mismatch) --

interface SalesSection {
  seriesGranularity?: 'day' | 'month';
  seriesBaseCurrency?: Array<{ date: string; total: string }>;
  totalsByCurrency?: Array<{ currencyCode: string; documentCount: number; total: string }>;
}
interface SellerRow { name: string; code: string; qtySold: number }
interface CustomerRow { name: string; currencyCode: string; invoiceCount: number; total: string }
interface AgingSection {
  totalsByCurrency?: Array<{ currencyCode: string; current: string; days_1_30: string; days_31_60: string; days_61_90: string; days_90_plus: string; total: string }>;
  topParties?: Array<{ party: string; currencyCode: string; total: string; overdue90Plus: string }>;
}
interface PnlSection {
  baseCurrencyCode?: string;
  topRevenueAccounts?: Array<{ account: string; amount: string }>;
  topExpenseAccounts?: Array<{ account: string; amount: string }>;
}
interface ProfitSection {
  byChannel?: Array<{ label?: string; key?: string; currencyCode?: string; revenue?: string; profit?: string }>;
  isEstimate?: boolean;
}
interface StockRow { name: string; code: string; quantity: number }
interface BalanceSection {
  baseCurrencyCode?: string;
  totalAssets?: string;
  totalLiabilities?: string;
  totalEquity?: string;
  topAssets?: Array<{ account: string; balance: string }>;
  topLiabilities?: Array<{ account: string; balance: string }>;
  topEquity?: Array<{ account: string; balance: string }>;
}

function section<T>(report: AiReport, name: string): T | null {
  const s = report.snapshot.sections?.[name];
  if (!s || typeof s !== 'object' || (s as { denied?: boolean }).denied) return null;
  return s as T;
}

// ---- shared bits ------------------------------------------------------------

interface Ctx {
  report: AiReport;
  /** Translator pinned to the REPORT's language — figures are part of the document. */
  t: TFunction;
  locale: string;
}

function money(ctx: Ctx, amount: string | number, currency: string): string {
  return formatMoney(String(amount), { locale: ctx.locale, currency });
}

function compact(ctx: Ctx, v: number): string {
  // Small values keep their fraction (a shop selling by kg/meter has qty 812.75
  // and the figure must not assert 813); integers still render bare.
  return formatNumber(v, { locale: ctx.locale }, {
    notation: Math.abs(v) >= 10_000 ? 'compact' : 'standard',
    maximumFractionDigits: Math.abs(v) >= 10_000 ? 1 : 2,
  });
}

function FigureShell({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }): JSX.Element {
  return (
    <figure className="rf-scope my-5 rounded-xl border border-border bg-bg-muted/40 p-4 print:break-inside-avoid">
      <figcaption className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-semibold">{title}</span>
        {subtitle && <span className="text-xs text-fg-muted">{subtitle}</span>}
      </figcaption>
      {children}
    </figure>
  );
}

const tooltipStyle = {
  background: CHART_COLORS.bg,
  border: `1px solid ${CHART_COLORS.border}`,
  borderRadius: 6,
  fontSize: 12,
  color: CHART_COLORS.fg,
} as const;

/** Horizontal single-series bar list: thin marks, rounded data end, ink labels. */
function HBars({ ctx, rows, currency }: {
  ctx: Ctx;
  rows: Array<{ label: string; value: number }>;
  /** Format values as money in this currency; absent = plain counts. */
  currency?: string;
}): JSX.Element {
  const fmt = (v: number): string => (currency ? money(ctx, v, currency) : compact(ctx, v));
  return (
    <div dir="ltr" style={{ height: rows.length * 30 + 12 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 64, left: 8, bottom: 0 }} barCategoryGap={6}>
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey="label"
            width={150}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: CHART_COLORS.fg }}
          />
          <Tooltip contentStyle={tooltipStyle} formatter={(v) => [fmt(Number(v ?? 0)), '']} cursor={{ fill: 'transparent' }} />
          <Bar dataKey="value" fill="var(--rf-1)" barSize={16} radius={[0, 4, 4, 0]} isAnimationActive={false}>
            <LabelList dataKey="value" position="right" formatter={(v) => fmt(Number(v ?? 0) || 0)} style={{ fontSize: 11, fill: CHART_COLORS.fgMuted }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function DataTable_({ head, rows }: { head: string[]; rows: Array<Array<React.ReactNode>> }): JSX.Element {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border text-xs uppercase tracking-wide text-fg-muted">
          {head.map((h, i) => <th key={i} className={`py-1.5 ${i === 0 ? 'text-start' : 'text-end'}`}>{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-border/50 last:border-0">
            {r.map((c, j) => <td key={j} className={`py-1.5 ${j === 0 ? 'text-start' : 'text-end font-mono tabular-nums'}`}>{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---- the figures ------------------------------------------------------------

function SalesTrend({ ctx }: { ctx: Ctx }): JSX.Element | null {
  const sales = section<SalesSection>(ctx.report, 'sales');
  const monthly = sales?.seriesGranularity === 'month';
  const data = (sales?.seriesBaseCurrency ?? []).map((d) => ({ date: d.date, total: Number(d.total) }));
  if (data.length < 2) return null;
  const base = ctx.report.snapshot.baseCurrencyCode;
  return (
    <FigureShell title={ctx.t('aiReports.figure.salesTrend', { defaultValue: 'Sales over time', currency: base })} subtitle={base}>
      <div className="h-52 w-full" dir="ltr">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 12, left: 8, bottom: 0 }}>
            <defs>
              <linearGradient id="rf-sales" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--rf-1)" stopOpacity={0.35} />
                <stop offset="95%" stopColor="var(--rf-1)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={CHART_COLORS.border} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: CHART_COLORS.fgMuted }} tickFormatter={(d: string) => (monthly ? d : d.slice(5))} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: CHART_COLORS.fgMuted }} tickFormatter={(v: number) => compact(ctx, v)} width={52} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => [money(ctx, Number(v ?? 0), base), '']} labelFormatter={(d) => String(d ?? '')} />
            <Area type="monotone" dataKey="total" stroke="var(--rf-1)" strokeWidth={2} fill="url(#rf-sales)" isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </FigureShell>
  );
}

function SalesTotals({ ctx }: { ctx: Ctx }): JSX.Element | null {
  const rows = section<SalesSection>(ctx.report, 'sales')?.totalsByCurrency ?? [];
  if (rows.length === 0) return null;
  return (
    <FigureShell title={ctx.t('aiReports.figure.salesTotals', { defaultValue: 'Sales by currency' })}>
      <DataTable_
        head={[
          ctx.t('aiReports.figure.th.currency', { defaultValue: 'Currency' }),
          ctx.t('aiReports.figure.th.documents', { defaultValue: 'Documents' }),
          ctx.t('aiReports.figure.th.total', { defaultValue: 'Total' }),
        ]}
        rows={rows.map((r) => [r.currencyCode, formatNumber(r.documentCount, { locale: ctx.locale }), money(ctx, r.total, r.currencyCode)])}
      />
    </FigureShell>
  );
}

function TopSellers({ ctx }: { ctx: Ctx }): JSX.Element | null {
  const rows = (section<SellerRow[]>(ctx.report, 'topSellers') ?? []).filter((r) => r && r.name);
  if (rows.length === 0) return null;
  return (
    <FigureShell title={ctx.t('aiReports.figure.topSellers', { defaultValue: 'Best sellers' })} subtitle={ctx.t('aiReports.figure.units', { defaultValue: 'units sold' })}>
      <HBars ctx={ctx} rows={rows.map((r) => ({ label: r.name, value: Number(r.qtySold) || 0 }))} />
    </FigureShell>
  );
}

/** One chart per currency — amounts of different currencies never share an axis. */
function TopCustomers({ ctx }: { ctx: Ctx }): JSX.Element | null {
  const rows = (section<CustomerRow[]>(ctx.report, 'topCustomers') ?? []).filter((r) => r && r.name);
  if (rows.length === 0) return null;
  const byCurrency = new Map<string, CustomerRow[]>();
  for (const r of rows) {
    byCurrency.set(r.currencyCode, [...(byCurrency.get(r.currencyCode) ?? []), r]);
  }
  return (
    <FigureShell title={ctx.t('aiReports.figure.topCustomers', { defaultValue: 'Top customers' })}>
      <div className="flex flex-col gap-3">
        {[...byCurrency.entries()].map(([currency, list]) => (
          <div key={currency}>
            {byCurrency.size > 1 && <div className="mb-1 text-xs font-medium text-fg-muted">{currency}</div>}
            <HBars ctx={ctx} rows={list.map((r) => ({ label: r.name, value: Number(r.total) || 0 }))} currency={currency} />
          </div>
        ))}
      </div>
    </FigureShell>
  );
}

/** Aging buckets: single-hue lightness ramp (current → 90+), one bar per currency. */
const AGING_BUCKETS = [
  { key: 'current', mix: 24 },
  { key: 'days_1_30', mix: 42 },
  { key: 'days_31_60', mix: 61 },
  { key: 'days_61_90', mix: 80 },
  { key: 'days_90_plus', mix: 100 },
] as const;

function bucketLabel(ctx: Ctx, key: string): string {
  const map: Record<string, [string, string]> = {
    current: ['aiReports.figure.bucket.current', 'Current'],
    days_1_30: ['aiReports.figure.bucket.d1_30', '1–30 d'],
    days_31_60: ['aiReports.figure.bucket.d31_60', '31–60 d'],
    days_61_90: ['aiReports.figure.bucket.d61_90', '61–90 d'],
    days_90_plus: ['aiReports.figure.bucket.d90', '90+ d'],
  };
  const [k, dv] = map[key] ?? ['', key];
  return k ? ctx.t(k, { defaultValue: dv }) : key;
}

function AgingFigure({ ctx, sectionName, titleKey, titleDefault }: {
  ctx: Ctx;
  sectionName: 'receivablesAging' | 'payablesAging';
  titleKey: string;
  titleDefault: string;
}): JSX.Element | null {
  const aging = section<AgingSection>(ctx.report, sectionName);
  const totals = (aging?.totalsByCurrency ?? []).filter((r) => Number(r.total) > 0);
  if (totals.length === 0) return null;
  const parties = aging?.topParties ?? [];
  return (
    <FigureShell title={ctx.t(titleKey, { defaultValue: titleDefault })}>
      {/* Legend once — identity never rides on color alone (labels + tooltips carry it too). */}
      <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-fg-muted">
        {AGING_BUCKETS.map((b) => (
          <span key={b.key} className="inline-flex items-center gap-1.5">
            <span aria-hidden className="h-2.5 w-2.5 rounded-sm" style={{ background: `color-mix(in oklab, var(--color-warning) ${b.mix}%, var(--color-bg))` }} />
            {bucketLabel(ctx, b.key)}
          </span>
        ))}
      </div>
      <div className="flex flex-col gap-2.5">
        {totals.map((row) => {
          const total = Number(row.total) || 1;
          return (
            <div key={row.currencyCode}>
              <div className="mb-1 flex items-baseline justify-between text-xs">
                <span className="font-medium text-fg-muted">{row.currencyCode}</span>
                <span className="font-mono tabular-nums">{money(ctx, row.total, row.currencyCode)}</span>
              </div>
              <div className="flex h-4 w-full gap-0.5 overflow-hidden rounded" dir="ltr">
                {AGING_BUCKETS.map((b) => {
                  const v = Number(row[b.key]) || 0;
                  if (v <= 0) return null;
                  return (
                    <span
                      key={b.key}
                      title={`${bucketLabel(ctx, b.key)}: ${money(ctx, v, row.currencyCode)}`}
                      className="h-full"
                      style={{
                        width: `${Math.max((v / total) * 100, 1.5)}%`,
                        background: `color-mix(in oklab, var(--color-warning) ${b.mix}%, var(--color-bg))`,
                      }}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {parties.length > 0 && (
        <div className="mt-3">
          <DataTable_
            head={[
              ctx.t('aiReports.figure.th.party', { defaultValue: 'Party' }),
              ctx.t('aiReports.figure.th.total', { defaultValue: 'Total' }),
              ctx.t('aiReports.figure.th.overdue90', { defaultValue: '90+ overdue' }),
            ]}
            rows={parties.map((p) => [
              `${p.party} · ${p.currencyCode}`,
              money(ctx, p.total, p.currencyCode),
              Number(p.overdue90Plus) > 0
                ? <span className="text-danger">{money(ctx, p.overdue90Plus, p.currencyCode)}</span>
                : '—',
            ])}
          />
        </div>
      )}
    </FigureShell>
  );
}

function PnlBreakdown({ ctx }: { ctx: Ctx }): JSX.Element | null {
  const pnl = section<PnlSection>(ctx.report, 'profitAndLoss');
  if (!pnl) return null;
  const base = pnl.baseCurrencyCode ?? ctx.report.snapshot.baseCurrencyCode;
  const rev = (pnl.topRevenueAccounts ?? []).map((r) => ({ label: r.account, value: Number(r.amount) || 0 }));
  const exp = (pnl.topExpenseAccounts ?? []).map((r) => ({ label: r.account, value: Number(r.amount) || 0 }));
  if (rev.length === 0 && exp.length === 0) return null;
  return (
    <FigureShell title={ctx.t('aiReports.figure.pnl', { defaultValue: 'Revenue & expense accounts' })} subtitle={base}>
      <div className="grid gap-4 sm:grid-cols-2">
        {rev.length > 0 && (
          <div>
            <div className="mb-1 text-xs font-medium text-fg-muted">{ctx.t('aiReports.figure.th.revenue', { defaultValue: 'Revenue' })}</div>
            <HBars ctx={ctx} rows={rev} currency={base} />
          </div>
        )}
        {exp.length > 0 && (
          <div>
            <div className="mb-1 text-xs font-medium text-fg-muted">{ctx.t('aiReports.figure.th.expenses', { defaultValue: 'Expenses' })}</div>
            <HBars ctx={ctx} rows={exp} currency={base} />
          </div>
        )}
      </div>
    </FigureShell>
  );
}

function channelLabel(ctx: Ctx, raw: string): string {
  const known = ['pos', 'online', 'field', 'manual'];
  return known.includes(raw)
    ? ctx.t(`aiReports.figure.channel.${raw}`, { defaultValue: raw })
    : raw;
}

/** The one two-series chart: revenue vs profit per channel, per currency. Legend + direct labels. */
function ProfitByChannel({ ctx }: { ctx: Ctx }): JSX.Element | null {
  const rows = (section<ProfitSection>(ctx.report, 'profit')?.byChannel ?? [])
    .filter((r) => r && typeof r === 'object' && r.currencyCode && (r.revenue != null || r.profit != null));
  if (rows.length === 0) return null;
  const byCurrency = new Map<string, typeof rows>();
  for (const r of rows) {
    byCurrency.set(r.currencyCode!, [...(byCurrency.get(r.currencyCode!) ?? []), r]);
  }
  const isEstimate = section<ProfitSection>(ctx.report, 'profit')?.isEstimate;
  const revLabel = ctx.t('aiReports.figure.th.revenue', { defaultValue: 'Revenue' });
  const profitLabel = ctx.t('aiReports.figure.th.profit', { defaultValue: 'Profit' });
  return (
    <FigureShell title={ctx.t('aiReports.figure.profitByChannel', { defaultValue: 'Revenue vs profit by channel' })}>
      <div className="mb-2 flex gap-4 text-xs text-fg-muted">
        <span className="inline-flex items-center gap-1.5"><span aria-hidden className="h-2.5 w-2.5 rounded-sm" style={{ background: 'var(--rf-1)' }} />{revLabel}</span>
        <span className="inline-flex items-center gap-1.5"><span aria-hidden className="h-2.5 w-2.5 rounded-sm" style={{ background: 'var(--rf-2)' }} />{profitLabel}</span>
      </div>
      <div className="flex flex-col gap-3">
        {[...byCurrency.entries()].map(([currency, list]) => {
          const data = list.map((r) => ({
            label: channelLabel(ctx, r.label ?? r.key ?? '?'),
            revenue: Number(r.revenue) || 0,
            profit: Number(r.profit) || 0,
          }));
          return (
            <div key={currency}>
              {byCurrency.size > 1 && <div className="mb-1 text-xs font-medium text-fg-muted">{currency}</div>}
              <div dir="ltr" style={{ height: data.length * 52 + 12 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data} layout="vertical" margin={{ top: 0, right: 72, left: 8, bottom: 0 }} barCategoryGap={10} barGap={2}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="label" width={110} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: CHART_COLORS.fg }} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v, name) => [money(ctx, Number(v ?? 0), currency), name === 'revenue' ? revLabel : profitLabel]} cursor={{ fill: 'transparent' }} />
                    <Bar dataKey="revenue" fill="var(--rf-1)" barSize={14} radius={[0, 4, 4, 0]} isAnimationActive={false}>
                      <LabelList dataKey="revenue" position="right" formatter={(v) => compact(ctx, Number(v ?? 0) || 0)} style={{ fontSize: 10, fill: CHART_COLORS.fgMuted }} />
                    </Bar>
                    <Bar dataKey="profit" fill="var(--rf-2)" barSize={14} radius={[0, 4, 4, 0]} isAnimationActive={false}>
                      <LabelList dataKey="profit" position="right" formatter={(v) => compact(ctx, Number(v ?? 0) || 0)} style={{ fontSize: 10, fill: CHART_COLORS.fgMuted }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        })}
      </div>
      {isEstimate && <p className="mt-2 text-xs text-fg-muted">{ctx.t('aiReports.figure.estimateNote', { defaultValue: 'Some costs are estimated at today’s prices.' })}</p>}
    </FigureShell>
  );
}

function LowStock({ ctx }: { ctx: Ctx }): JSX.Element | null {
  const rows = (section<StockRow[]>(ctx.report, 'lowStock') ?? []).filter((r) => r && r.name);
  if (rows.length === 0) return null;
  return (
    <FigureShell title={ctx.t('aiReports.figure.lowStock', { defaultValue: 'Items running low' })}>
      <DataTable_
        head={[
          ctx.t('aiReports.figure.th.item', { defaultValue: 'Item' }),
          ctx.t('aiReports.figure.th.code', { defaultValue: 'Code' }),
          ctx.t('aiReports.figure.th.qty', { defaultValue: 'On hand' }),
        ]}
        rows={rows.map((r) => [
          r.name,
          <span key="c" className="text-fg-muted">{r.code}</span>,
          <span key="q" className={Number(r.quantity) <= 0 ? 'text-danger' : undefined}>{formatNumber(Number(r.quantity) || 0, { locale: ctx.locale })}</span>,
        ])}
      />
    </FigureShell>
  );
}

function BalanceSheetFigure({ ctx }: { ctx: Ctx }): JSX.Element | null {
  const bs = section<BalanceSection>(ctx.report, 'balanceSheet');
  if (!bs) return null;
  const base = bs.baseCurrencyCode ?? ctx.report.snapshot.baseCurrencyCode;
  const block = (label: string, total: string | undefined, rows: Array<{ account: string; balance: string }> | undefined): Array<Array<React.ReactNode>> => [
    [<strong key="h">{label}</strong>, total != null ? <strong key="t">{money(ctx, total, base)}</strong> : ''],
    ...(rows ?? []).map((r) => [<span key="a" className="ps-3 text-fg-muted">{r.account}</span>, money(ctx, r.balance, base)]),
  ];
  return (
    <FigureShell title={ctx.t('aiReports.figure.balanceSheet', { defaultValue: 'Balance sheet position' })} subtitle={base}>
      <DataTable_
        head={[ctx.t('aiReports.figure.th.account', { defaultValue: 'Account' }), ctx.t('aiReports.figure.th.amount', { defaultValue: 'Amount' })]}
        rows={[
          ...block(ctx.t('aiReports.figure.th.assets', { defaultValue: 'Assets' }), bs.totalAssets, bs.topAssets),
          ...block(ctx.t('aiReports.figure.th.liabilities', { defaultValue: 'Liabilities' }), bs.totalLiabilities, bs.topLiabilities),
          ...block(ctx.t('aiReports.figure.th.equity', { defaultValue: 'Equity' }), bs.totalEquity, bs.topEquity),
        ]}
      />
    </FigureShell>
  );
}

// ---- registry ---------------------------------------------------------------

const RENDERERS: Record<AiReportFigureId, (ctx: Ctx) => JSX.Element | null> = {
  sales_trend: (ctx) => <SalesTrend ctx={ctx} />,
  sales_totals: (ctx) => <SalesTotals ctx={ctx} />,
  top_sellers: (ctx) => <TopSellers ctx={ctx} />,
  top_customers: (ctx) => <TopCustomers ctx={ctx} />,
  receivables_aging: (ctx) => <AgingFigure ctx={ctx} sectionName="receivablesAging" titleKey="aiReports.figure.arAging" titleDefault="Receivables aging" />,
  payables_aging: (ctx) => <AgingFigure ctx={ctx} sectionName="payablesAging" titleKey="aiReports.figure.apAging" titleDefault="Payables aging" />,
  pnl_breakdown: (ctx) => <PnlBreakdown ctx={ctx} />,
  profit_by_channel: (ctx) => <ProfitByChannel ctx={ctx} />,
  low_stock: (ctx) => <LowStock ctx={ctx} />,
  balance_sheet: (ctx) => <BalanceSheetFigure ctx={ctx} />,
};

/**
 * Build every figure this report supports, keyed by marker id. The list comes
 * from the same shared `availableAiReportFigures` the generation prompt used,
 * so the model can only place markers the viewer can honor.
 */
export function buildReportFigures(ctx: Ctx): Map<AiReportFigureId, JSX.Element> {
  const out = new Map<AiReportFigureId, JSX.Element>();
  for (const id of availableAiReportFigures(ctx.report.snapshot)) {
    const node = RENDERERS[id](ctx);
    if (node) out.set(id, node);
  }
  return out;
}
